import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { API_CONFIG } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { useSocketReconnect } from './useSocketReconnect'

type SocketEventMap = Record<string, (payload: never) => void>

interface UseAuthedSocketOptions<E extends SocketEventMap> {
  events: E
  reconnectQueryKeys?: Array<string | ReadonlyArray<string | number>>
}

export function useAuthedSocket<E extends SocketEventMap>(
  namespace: string,
  opts: UseAuthedSocketOptions<E>,
): { socket: Socket | null } {
  const { token, user } = useAuth()
  const socketRef = useRef<Socket | null>(null)
  const [socket, setSocket] = useState<Socket | null>(null)
  // Reconecta só se o escopo mudar: setUser(profile) gera objeto novo a cada
  // refresh e derrubava/reabria o socket em toda carga.
  const userKey = user ? `${user.id}|${user.user_type_code ?? ''}|${user.cost_center_id ?? ''}` : null

  useEffect(() => {
    if (!token || !userKey) {
      // Logout: garante que nenhuma conexão fique aberta.
      socketRef.current?.close()
      socketRef.current = null
      setSocket(null)
      return
    }
    const s = io(`${API_CONFIG.BASE_URL}${namespace}`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: { token },
    })
    socketRef.current = s
    setSocket(s)
    for (const [event, handler] of Object.entries(opts.events)) {
      s.on(event, handler as (payload: unknown) => void)
    }
    return () => {
      for (const [event, handler] of Object.entries(opts.events)) {
        s.off(event, handler as (payload: unknown) => void)
      }
      s.close()
      socketRef.current = null
      setSocket(null)
    }
     
  }, [token, userKey, namespace])

  useSocketReconnect(socket, opts.reconnectQueryKeys ?? [])

  return { socket }
}
